import { lazy, Suspense, useEffect } from "react";
import { Route, Routes, useLocation, useParams } from "react-router-dom";
import { demoCatalogById } from "./data/demoCatalog";
import { trackPageView } from "./lib/analytics";
import { resolveSeo } from "./seo";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";

const Approach = lazy(() => import("./pages/Approach"));
const Solutions = lazy(() => import("./pages/Solutions"));
const About = lazy(() => import("./pages/About"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const Showcase = lazy(() => import("./pages/Showcase"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Contact = lazy(() => import("./pages/Contact"));
const Control = lazy(() => import("./pages/Control"));
const DemoViewer = lazy(() => import("./pages/DemoViewer"));
const DiagnoseApp = lazy(() => import("./diagnose/DiagnoseApp"));

function DemoRoute() {
  const { demoId = "" } = useParams();
  return demoCatalogById.has(demoId) ? <DemoViewer /> : <NotFound />;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const seo = resolveSeo(pathname);
    document.title = seo.title;
    document.querySelectorAll<HTMLMetaElement>('meta[name="description"]').forEach((element) => element.setAttribute("content", seo.description));
    document.querySelector<HTMLMetaElement>('meta[name="robots"]')?.setAttribute("content", seo.robots);
    document.querySelector<HTMLMetaElement>('meta[property="og:title"]')?.setAttribute("content", seo.title);
    document.querySelectorAll<HTMLMetaElement>('meta[property="og:description"]').forEach((element) => element.setAttribute("content", seo.description));
    document.querySelector<HTMLMetaElement>('meta[property="og:url"]')?.setAttribute("content", seo.canonicalUrl ?? `${window.location.origin}${pathname}`);
    document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]')?.setAttribute("content", seo.title);
    document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]')?.setAttribute("content", seo.description);

    const existingCanonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (seo.canonicalUrl) {
      const canonical = existingCanonical ?? document.head.appendChild(document.createElement("link"));
      canonical.rel = "canonical";
      canonical.href = seo.canonicalUrl;
    } else {
      existingCanonical?.remove();
    }

    if (seo.analyticsPath) trackPageView(seo.analyticsPath, seo.title);
    let cancelled = false;
    let retryTimer: number | undefined;
    if (hash) {
      let attempts = 0;
      const scrollToHash = () => {
        if (cancelled) return;
        const target = document.querySelector(hash);
        if (target) {
          target.scrollIntoView({ block: "start", behavior: "auto" });
          return;
        }
        attempts += 1;
        if (attempts < 30) retryTimer = window.setTimeout(scrollToHash, 50);
      };
      window.requestAnimationFrame(scrollToHash);
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [pathname, hash]);
  return null;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<div className="route-loader" role="status">در حال آماده‌سازی…</div>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/approach" element={<Approach />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/about" element={<About />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/showcase" element={<Showcase />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/diagnose/*" element={<DiagnoseApp />} />
          <Route path="/__control" element={<Control />} />
          <Route path="/showcase/:demoId" element={<DemoRoute />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}
